import React, { useState } from 'react';
import { renderMarkdown } from '../../markdown';

/* Scaffolding components (registry §4): hints, concepts, worked examples. */

// --------------------------- hint_panel --------------------------------------
// A couple of compact buttons directly below the question — no side rail.
const Bulb = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 18h6" /><path d="M10 22h4" />
    <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.4 1 2.3h6c0-.9.4-1.8 1-2.3A7 7 0 0 0 12 2Z" />
  </svg>
);

export const HintPanel = ({ instance, emit, busy }) => {
  const { props, status } = instance;
  const unlocked = props.unlocked || [];
  const nextLevel = unlocked.length + 1;
  return (
    <div className="tu-hintrow">
      {unlocked.map((h) => (
        <div key={h.level} className="tu-hint inline">
          <span className="tu-hintlvl"><Bulb /> Hint {h.level}</span>
          {renderMarkdown(h.text_md || '')}
        </div>
      ))}
      {nextLevel <= props.total_levels && status === 'active' && (
        <button type="button" className="tu-hintchip" disabled={busy}
          onClick={() => emit('hint_requested', { level: nextLevel })}>
          <Bulb /> Hint
          <span className="tu-hintcount">{unlocked.length}/{props.total_levels}</span>
        </button>
      )}
    </div>
  );
};

// --------------------------- concept_card ------------------------------------
export const ConceptCard = ({ instance, emit }) => {
  const { props } = instance;
  const [open, setOpen] = useState(!props.start_collapsed);
  return (
    <div className="tu-card tu-concept">
      <button type="button" className="tu-concept-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="tu-concept-title">📘 {props.title}</span>
        <span className="tu-caret">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="tu-concept-body">
          {renderMarkdown(props.definition_md || '')}
          {props.example_md && (
            <div className="tu-concept-sec"><b>Example</b>{renderMarkdown(props.example_md)}</div>
          )}
          {(props.pitfalls || []).length > 0 && (
            <div className="tu-concept-sec">
              <b>Pitfalls</b>
              <ul>{props.pitfalls.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </div>
          )}
          {(props.related || []).length > 0 && (
            <div className="tu-concept-rel">
              {props.related.map((r) => (
                <button key={r.id} type="button" className="tu-chip"
                  onClick={() => emit('link_clicked', { concept_id: r.id })}>
                  {r.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --------------------------- worked_example_stepper --------------------------
export const WorkedExampleStepper = ({ instance, emit, busy }) => {
  const { props } = instance;
  const steps = props.steps_unlocked || [];
  const nextStep = steps.length + 1;
  const [prediction, setPrediction] = useState('');
  const needsPrediction = props.predict_mode && nextStep <= props.total_steps;

  return (
    <div className="tu-card tu-stepper">
      <div className="tu-stepper-title">🪜 {props.title}</div>
      <div className="tu-stepper-progress">Step {steps.length}/{props.total_steps}</div>
      {steps.map((s) => (
        <div key={s.i} className="tu-step">
          <span className="tu-stepnum">{s.i}</span>
          <div>
            {renderMarkdown(s.content_md || '')}
            {s.prediction_feedback_md && (
              <div className="tu-predfb">{renderMarkdown(s.prediction_feedback_md)}</div>
            )}
          </div>
        </div>
      ))}
      {nextStep <= props.total_steps && (
        needsPrediction ? (
          <div className="tu-predict">
            <input className="tu-input" placeholder={`What happens in step ${nextStep}? Predict first…`}
              value={prediction} disabled={busy}
              onChange={(e) => setPrediction(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && prediction.trim()) {
                  emit('prediction_submitted', { step: nextStep, text: prediction });
                  setPrediction('');
                }
              }} />
            <button type="button" className="tu-primary" disabled={busy || !prediction.trim()}
              onClick={() => { emit('prediction_submitted', { step: nextStep, text: prediction }); setPrediction(''); }}>
              Predict → reveal
            </button>
          </div>
        ) : (
          <button type="button" className="tu-hintbtn" disabled={busy}
            onClick={() => emit('step_advance_requested', { next_step: nextStep })}>
            Next step →
          </button>
        )
      )}
    </div>
  );
};
